// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';

import Content from 'components/Content';
import Section from 'components/Section';
import {
    featureAnnouncementRegistry,
    formatFeatureAnnouncementDate,
    loadFeatureAnnouncements,
    resolveFeatureAnnouncementCtaUrl,
} from './newFeatures/featureAnnouncements';

const renderAnnouncementHtml = (content) => DOMPurify.sanitize(marked(content || ''));
const isExternalUrl = (url) => /^https?:\/\//.test(url);

const NewFeatures = () => {
    const { organisationId } = useParams();
    const [announcements, setAnnouncements] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;

        loadFeatureAnnouncements()
            .then((nextAnnouncements) => {
                if (!active) {
                    return;
                }

                setAnnouncements(nextAnnouncements);
                setError(null);
            })
            .catch((nextError) => {
                if (!active) {
                    return;
                }

                setAnnouncements([]);
                setError(nextError);
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, []);

    const hasAnnouncements = announcements.length > 0;
    const registryCount = featureAnnouncementRegistry.length;
    const registryCountLabel = `${registryCount} ${registryCount === 1 ? 'opdatering' : 'opdateringer'} i registret`;

    return (
        <Content>
            <Box sx={{ width: '100%', maxWidth: 1080 }}>
                <Section
                    title="Nye features"
                    description={ (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={ 1 } alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Typography variant="body2">
                                Her kan du følge med i nye produktforbedringer og funktioner i Teal Business.
                            </Typography>
                            <Chip size="small" label={ registryCountLabel } />
                        </Stack>
                    ) }
                >
                    { loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress color="primary" size={ 32 } />
                        </Box>
                    ) }

                    { !loading && error && (
                        <Alert severity="error">
                            Kunne ikke indlæse feature-opdateringer lige nu.
                        </Alert>
                    ) }

                    { !loading && !error && !hasAnnouncements && (
                        <Typography variant="body1">
                            Der er ikke nogen nye features at vise endnu.
                        </Typography>
                    ) }

                    { !loading && !error && hasAnnouncements && (
                        <Box
                            sx={{
                                maxHeight: { xs: 'none', md: 720 },
                                overflowY: { xs: 'visible', md: 'auto' },
                                pr: { xs: 0, md: 2 },
                                mt: 1,
                            }}
                        >
                            <Timeline
                                position="right"
                                sx={{
                                    m: 0,
                                    p: 0,
                                    '& .MuiTimelineItem-root:before': {
                                        flex: 0,
                                        padding: 0,
                                    },
                                }}
                            >
                                { announcements.map((announcement, index) => {
                                    const ctaUrl = resolveFeatureAnnouncementCtaUrl(announcement.ctaUrl, { organisationId });
                                    const hasCta = announcement.ctaLabel && ctaUrl;
                                    const openExternally = announcement.ctaExternal || isExternalUrl(ctaUrl);

                                    return (
                                        <TimelineItem key={ announcement.id }>
                                            <TimelineOppositeContent
                                                sx={{
                                                    display: { xs: 'none', md: 'block' },
                                                    flex: 0.22,
                                                    pt: 1.5,
                                                    pr: 2,
                                                }}
                                                color="text.secondary"
                                            >
                                                { formatFeatureAnnouncementDate(announcement.publishedAt) }
                                            </TimelineOppositeContent>
                                            <TimelineSeparator>
                                                <TimelineDot color="primary" variant="outlined" />
                                                { index < announcements.length - 1 && <TimelineConnector /> }
                                            </TimelineSeparator>
                                            <TimelineContent sx={{ py: 0, px: { xs: 2, md: 3 }, pb: 3 }}>
                                                <Box
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 2,
                                                        p: { xs: 2, md: 3 },
                                                        backgroundColor: 'background.paper',
                                                    }}
                                                >
                                                    <Stack spacing={ 2 }>
                                                        <Stack spacing={ 1 }>
                                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={ 1 } alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                                                <Typography variant="h6">
                                                                    { announcement.title }
                                                                </Typography>
                                                                <Chip
                                                                    size="small"
                                                                    label={ formatFeatureAnnouncementDate(announcement.publishedAt) }
                                                                    sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                                                                />
                                                            </Stack>
                                                            <Typography variant="body2" color="text.secondary">
                                                                { announcement.summary }
                                                            </Typography>
                                                        </Stack>

                                                        <Box
                                                            sx={{
                                                                '& h1, & h2, & h3': { mt: 0 },
                                                                '& p:last-child': { mb: 0 },
                                                                '& ul, & ol': { pl: 3 },
                                                                '& a': { color: 'primary.main' },
                                                            }}
                                                            dangerouslySetInnerHTML={{ __html: renderAnnouncementHtml(announcement.content) }}
                                                        />

                                                        { hasCta && (
                                                            openExternally ? (
                                                                <Button component={ Link } href={ ctaUrl } target="_blank" rel="noreferrer" variant="contained">
                                                                    { announcement.ctaLabel }
                                                                </Button>
                                                            ) : (
                                                                <Button component={ RouterLink } to={ ctaUrl } variant="contained">
                                                                    { announcement.ctaLabel }
                                                                </Button>
                                                            )
                                                        ) }
                                                    </Stack>
                                                </Box>
                                            </TimelineContent>
                                        </TimelineItem>
                                    );
                                }) }
                            </Timeline>
                        </Box>
                    ) }
                </Section>
            </Box>
        </Content>
    );
};

export default NewFeatures;
